/**
 * «Полёт птицы» (GEN-18148 / GEN-18150) — единый код игры для ТВ (1t.gg) и
 * мобильных приложений. ТВ подключает модуль напрямую
 * (`BirdFlightDisplayEngine`), приложения получают его собранным в один файл
 * (`src/pages/bird-flight/main.ts`, `npm run build:bird-flight-app`) и зовут
 * через `window.groove*`. Правки игры — только здесь, иначе iOS, Android и ТВ
 * разъедутся.
 *
 * Мир low-poly, собран из примитивов в коде — без моделей и текстур. Поза —
 * четыре точки 0…1 (y вниз) в зеркальном кадре фронтальной камеры: плечи
 * и запястья. Птица повторяет руки игрока: раскрыл руки — крылья раскрыты и
 * птица медленно планирует; взмах вниз — подъём; руки сложены — пике;
 * одна рука выше другой — поворот.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { buildEkibastuzWorld, type BirdFlightWorld } from './ekibastuzWorld.ts';
import { buildAstanaWorld } from './astanaWorld.ts';
import { buildAlmatyWorld } from './almatyWorld.ts';
import { buildBarcelonaWorld } from './barcelonaWorld.ts';
import { buildParisWorld } from './parisWorld.ts';

/**
 * Все карты игры; новая карта добавляется сюда и в `builders`. Каньон
 * «Долина» отсюда ушёл — теперь это отдельная игра (Sky Race).
 */
export const BIRD_FLIGHT_MAPS = ['ekibastuz', 'astana', 'almaty', 'barcelona', 'paris'] as const;
export type BirdFlightMap = typeof BIRD_FLIGHT_MAPS[number];
/** Карта без выбора или с неизвестным id (старое приложение прислало `valley`). */
export const BIRD_FLIGHT_DEFAULT_MAP: BirdFlightMap = 'ekibastuz';

export interface BirdFlightPoint {
  readonly x: number;
  readonly y: number;
}

export interface BirdFlightPose {
  readonly leftShoulder: BirdFlightPoint;
  readonly rightShoulder: BirdFlightPoint;
  readonly leftWrist: BirdFlightPoint;
  readonly rightWrist: BirdFlightPoint;
}

export interface BirdFlightResult {
  readonly stars: number;
  readonly distance: number;
  readonly flaps: number;
  /** Время в полёте без пауз, секунд, 1…3600 — для отчёта о забеге. */
  readonly durationSeconds: number;
  /** Начало забега, ISO-8601 — идентичность забега в отчёте; повтор отчёта шлёт то же значение. */
  readonly startedAt: string;
}

/** Потолок `durationSeconds` в отчёте о забеге (сервер отвергает больше). */
const MAX_REPORTED_SECONDS = 3600;

/**
 * Птица из файла (сгенерирована по референсу): модель стоит и смотрит в +Z.
 * `pitch` — на сколько наклонить вперёд, чтобы легла в позу полёта.
 */
export interface BirdFlightModel {
  readonly url: string;
  readonly pitch: number;
}

/** Подсказки первых секунд забега, на языке приложения. */
export interface BirdFlightHints {
  readonly glide?: string;
  readonly flap?: string;
  readonly dive?: string;
  readonly turn?: string;
}

export interface BirdFlightOptions {
  /** Куда встроить canvas и HUD. Размер игры = размер элемента. */
  readonly host: HTMLElement;
  /** Слабым ТВ — 1 и без сглаживания. */
  readonly pixelRatio?: number;
  readonly antialias?: boolean;
  /**
   * Телефон: если кадры идут медленнее ~45 fps (нагрев, слабое железо),
   * разрешение ступенями снижается до 1.
   */
  readonly adaptiveQuality?: boolean;
  /** Управление стрелками и пробелом — для отладки в браузере. */
  readonly keyboard?: boolean;
  /** Шаблон «{n} м» на языке приложения. */
  readonly metersTemplate?: string;
  readonly hints?: BirdFlightHints;
  /** Карта открытого мира; нет или неизвестна — `BIRD_FLIGHT_DEFAULT_MAP`. */
  readonly map?: BirdFlightMap;
  /** Модель птицы (.glb) вместо птицы из примитивов. */
  readonly birdModel?: BirdFlightModel;
  readonly onCollect?: () => void;
  readonly onFinished?: (result: BirdFlightResult) => void;
  /** Постройка карты, 0…1 — для экрана загрузки. */
  readonly onLoadProgress?: (progress: number) => void;
  /** Карта построена, `start()` отработает сразу. */
  readonly onWorldReady?: () => void;
}

export interface BirdFlightGame {
  setPose(pose: BirdFlightPose): void;
  /** Новый забег с начала. */
  start(): void;
  pause(): void;
  resume(): void;
  /** Мир на старт, забег стоит до `start()`. */
  reset(): void;
  /**
   * Закончить забег: итог уходит в `onFinished`. Повторный вызов до нового
   * забега ничего не делает. Собранная последняя звезда заканчивает забег сама.
   */
  finish(): void;
  isRunning(): boolean;
  /** Карта построена. До этого `start()` запоминается и выполнится после. */
  isReady(): boolean;
  resize(): void;
  destroy(): void;
}

const STYLE_ID = 'bird-flight-style';
const STYLE = `
  .bf-root { position: absolute; inset: 0; overflow: hidden; background: #cfe9f8;
    font-family: -apple-system, system-ui, sans-serif; }
  .bf-root canvas { display: block; width: 100%; height: 100%; }
  .bf-hud { position: absolute; inset: 0; pointer-events: none; color: #fff;
    text-shadow: 0 2px 4px rgba(0,0,0,.5); font-weight: 800; }
  .bf-score { position: absolute; top: 16px; left: 16px; font-size: 22px; transform-origin: left center; }
  .bf-score.bf-bump { animation: bf-bump .35s ease-out; }
  @keyframes bf-bump { 0% { transform: scale(1); } 35% { transform: scale(1.45); color: #ffd23f; }
    100% { transform: scale(1); } }
  .bf-dist { position: absolute; top: 16px; right: 16px; font-size: 22px; color: #ff7a59; }
  .bf-dot { position: absolute; width: 28px; height: 28px; margin: -14px 0 0 -14px; border-radius: 50%;
    border: 3px solid #fff; background: rgba(107,220,107,.85); box-shadow: 0 0 12px rgba(107,220,107,.9);
    pointer-events: none; display: none; left: 0; top: 0; will-change: transform; }
  .bf-flaps { position: absolute; top: 48px; left: 16px; font-size: 18px; transform-origin: left center; }
  .bf-flaps.bf-bump { animation: bf-bump .3s ease-out; }
  .bf-alt { position: absolute; right: 22px; top: 25%; bottom: 25%; width: 10px; border-radius: 5px;
    background: rgba(255,255,255,.35); box-shadow: 0 1px 4px rgba(0,0,0,.25); }
  .bf-alt-mark { position: absolute; left: -5px; width: 20px; height: 20px; margin-top: -10px; border-radius: 50%;
    background: #fff; border: 3px solid #479fec; box-sizing: border-box; }
  .bf-alt-text { position: absolute; right: 22px; top: 50%; transform: translateY(-50%); white-space: nowrap;
    font-size: 16px; font-weight: 700; color: #fff; text-shadow: 0 1px 3px rgba(0,0,0,.6); }
  .bf-alt.bf-low .bf-alt-mark { border-color: #ff7a59; animation: bf-blink .5s steps(2) infinite; }
  @keyframes bf-blink { 50% { opacity: .35; } }
  .bf-hint { position: absolute; left: 50%; bottom: 14%; transform: translateX(-50%); padding: 10px 22px;
    border-radius: 999px; background: rgba(0,0,0,.35); font-size: 24px; white-space: nowrap;
    opacity: 0; transition: opacity .4s; }
  .bf-hint.bf-show { opacity: 1; }
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE;
  document.head.appendChild(style);
}

function el(tag: string, className: string, parent: HTMLElement): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  parent.appendChild(node);
  return node;
}

/**
 * Сливает все меши внутри `group` в один меш в координатах `group`: сотни
 * объектов → один вызов отрисовки. С `tint` цвет материала каждого меша
 * переезжает в цвет вершин (материал результата — с `vertexColors`), без —
 * все меши должны быть одного материала.
 */
function bake(group: THREE.Object3D, material: THREE.Material, tint: boolean): THREE.Mesh {
  group.updateMatrixWorld(true);
  const toLocal = new THREE.Matrix4().copy(group.matrixWorld).invert();
  const parts: THREE.BufferGeometry[] = [];
  group.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const geo = (obj.geometry.index ? obj.geometry.toNonIndexed() : obj.geometry.clone()) as THREE.BufferGeometry;
    geo.clearGroups();
    geo.deleteAttribute('uv');
    geo.applyMatrix4(new THREE.Matrix4().multiplyMatrices(toLocal, obj.matrixWorld));
    if (tint) {
      const { color } = obj.material as THREE.MeshLambertMaterial;
      const { count } = geo.getAttribute('position');
      const colors = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) colors.set([color.r, color.g, color.b], i * 3);
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }
    parts.push(geo);
  });
  const merged = mergeGeometries(parts, false);
  parts.forEach((p) => p.dispose());
  return new THREE.Mesh(merged, material);
}

export function createBirdFlightGame(options: BirdFlightOptions): BirdFlightGame {
  ensureStyle();
  const root = el('div', 'bf-root', options.host);
  const hudRoot = el('div', 'bf-hud', root);
  const hud = {
    score: el('div', 'bf-score', hudRoot),
    dist: el('div', 'bf-dist', hudRoot),
    flaps: el('div', 'bf-flaps', hudRoot),
    alt: el('div', 'bf-alt', hudRoot),
    hint: el('div', 'bf-hint', hudRoot),
  };
  const altMark = el('div', 'bf-alt-mark', hud.alt);
  const altText = el('span', 'bf-alt-text', altMark);
  const dotL = el('div', 'bf-dot', root);
  const dotR = el('div', 'bf-dot', root);
  const width = (): number => Math.max(1, root.clientWidth);
  const height = (): number => Math.max(1, root.clientHeight);
  const metersTemplate = options.metersTemplate ?? '{n} м';
  const meters = (n: number): string => metersTemplate.replace('{n}', String(n));
  let destroyed = false;
  let rafId = 0;
  // Вне забега мир стоит — перерисовываем, только если что-то поменялось
  // (сброс, ресайз, пауза). Иначе WebGL крутился бы 60 раз/с под калибровкой
  // и меню паузы и грел телефон вместе с Vision.
  let needsRender = true;

  // ─── Настройки ───────────────────────────────────────────────────────────
  const CFG = {
    speed: 11, // м/с вперёд
    boostSpeed: 18,
    smoothing: 4, // чем больше, тем резче птица следует за вводом
    dayCycleSeconds: 180, // открытый мир: полный цикл день → ночь → день
    // Высота — как у настоящей птицы. Раскрытие рук (0 — сложены, 1 —
    // раскрыты) задаёт скорость снижения: от пике до планирования. Взмах
    // вниз — один толчок вверх, сила по размаху.
    flapLift: 7.5, // прирост вертикальной скорости на ширину плеч размаха
    flapStrokePower: 1.3, // >1: сильный размах даёт непропорционально больше, чем слабый
    flapStreakWindow: 1.8, // с: взмах не позже этого после прошлого продолжает серию (живой темп ~1 с)
    flapStreakGain: 0.35, // прибавка к силе за каждый взмах серии
    flapStreakMax: 1.2, // потолок прибавки (+120 % на серии из 4 и более взмахов)
    flapMinStroke: 0.45, // ход рук вниз в ширинах плеч, меньше — дрожь, не взмах
    flapMaxStroke: 2.2, // потолок одного взмаха
    flapRearm: 0.25, // насколько поднять руки, чтобы засчитать следующий взмах
    // Турбо: круги руками (запястье вокруг своего плеча, как мельница).
    // Считается угол, пройденный запястьем, с памятью ~turboSpinMemory с:
    // ровный круг в секунду даёт ~5 рад. Взмах обеими руками вниз-вверх
    // ходит туда-обратно и сам себя гасит — турбо не включает.
    turboSpeed: 14, // прибавка скорости вперёд на полном турбо, м/с
    turboSpinStart: 2.5, // рад — с этого турбо начинается (~полкруга в секунду)
    turboSpinFull: 5.5, // рад — полное турбо (~круг в секунду)
    turboSpinMemory: 0.8, // с — сколько помним пройденный угол
    turboMinRadius: 0.35, // запястье ближе к плечу (в ширинах плеч) — дрожь, не круг
    turboFov: 14, // на сколько градусов расширяется обзор на полном турбо
    glideSink: 1, // снижение с раскрытыми руками, м/с
    diveSink: 10, // снижение со сложенными руками, м/с
    sinkResponse: 1.6, // как быстро вертикальная скорость приходит к целевой
    diveSpeedBonus: 7, // прибавка скорости вперёд в пике
    spreadClosed: 1.3, // расстояние между запястьями в ширинах плеч — руки сложены
    spreadOpen: 2.8, // — руки раскрыты
    foldedWingScale: 0.35, // размах сложенного крыла от полного
    hintSeconds: 8,
    turnRate: 1.3, // открытый мир: рад/с поворота при полном крене
    worldSpeedScale: 1.7, // открытый мир большой — летим быстрее
    worldMinAltitude: 4, // ниже над землёй не опускаемся
    birdSpan: 5.4, // размах птицы из файла — как у птицы из примитивов
    birdShoulder: 0.28, // где у модели кончается тело: доля полуразмаха
    lowAltitude: 4, // ниже — предупреждение у земли
    // Поза не приходила дольше — игрок вне кадра: крен в ноль, взмах с нуля.
    poseTimeoutMs: 300,
    // Послабления, чтобы ребёнку было проще:
    rollDeadzone: 0.12, // небольшой наклон рук не поворачивает — легче лететь прямо
    pickupRadius: 3.2, // было 2.2
    magnetRadius: 9, // ближе этого яблоко само тянется к птице
    magnetStrength: 10,
    maxClimb: 18,
    wingSwing: 0.9, // размах крыла при руке, поднятой на ширину плеч, рад
  };

  // ─── Сцена ───────────────────────────────────────────────────────────────
  const renderer = new THREE.WebGLRenderer({ antialias: options.antialias ?? true });
  let pixelRatio = options.pixelRatio ?? Math.min(devicePixelRatio, 2);
  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(width(), height(), false);
  root.insertBefore(renderer.domElement, hudRoot);

  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(62, width() / height(), 0.1, 600);

  // Low-poly: плоское затенение, без текстур.
  const mat = (color) => new THREE.MeshLambertMaterial({ color, flatShading: true });
  const rand = (min, max) => min + Math.random() * (max - min);

  // Статичное собирается в один меш на кусок (`bake`), цвета — в вершинах.
  const bakedMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });

  // Собираем звёзды: золотые, объёмные, крутятся — как монетки на референсе.
  const starShape = (() => {
    const shape = new THREE.Shape();
    for (let i = 0; i < 10; i++) {
      const r = i % 2 ? 0.45 : 1;
      const a = (i / 10) * Math.PI * 2 + Math.PI / 2;
      const x = Math.cos(a) * r; const
        y = Math.sin(a) * r;
      i ? shape.lineTo(x, y) : shape.moveTo(x, y);
    }
    return shape;
  })();
  const starGeo = new THREE.ExtrudeGeometry(starShape, {
    depth: 0.3, bevelEnabled: true, bevelThickness: 0.08, bevelSize: 0.08, bevelSegments: 1,
  });
  starGeo.center();
  const starMat = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0x8a5a00, flatShading: true });

  // ─── Эффект сбора звезды ──────────────────────────────────────────────────
  // Звезда-двойник вспыхивает и схлопывается, искры разлетаются и гаснут
  // уменьшением (не прозрачностью — не нужны клоны материалов), счётчик
  // подпрыгивает, приложение даёт лёгкую вибрацию.
  const effects = [];
  const sparkGeo = new THREE.OctahedronGeometry(0.22, 0);
  const sparkMats = [0xffe27a, 0xffffff, 0xffc93c].map((color) => new THREE.MeshBasicMaterial({ color }));

  function collectEffect(worldPosition) {
    const flash = new THREE.Mesh(starGeo, starMat);
    flash.position.copy(worldPosition);
    scene.add(flash);
    effects.push({
      mesh: flash, age: 0, life: 0.35, kind: 'flash',
    });

    for (let i = 0; i < 10; i++) {
      const spark = new THREE.Mesh(sparkGeo, sparkMats[i % sparkMats.length]);
      spark.position.copy(worldPosition);
      const dir = new THREE.Vector3(rand(-1, 1), rand(-0.3, 1), rand(-1, 1)).normalize();
      scene.add(spark);
      effects.push({
        mesh: spark, age: 0, life: rand(0.35, 0.6), kind: 'spark', velocity: dir.multiplyScalar(rand(6, 11)),
      });
    }

    hud.score.classList.remove('bf-bump');
    void hud.score.offsetWidth; // перезапуск CSS-анимации при частом сборе
    hud.score.classList.add('bf-bump');
    options.onCollect?.();
  }

  function updateEffects(dt) {
    for (let i = effects.length - 1; i >= 0; i--) {
      const e = effects[i];
      e.age += dt;
      const t = Math.min(1, e.age / e.life);
      if (e.kind === 'flash') {
        // Раздувается и тут же схлопывается, быстро крутясь.
        const scale = t < 0.4 ? 1.1 + t * 2.5 : (1 - t) / 0.6 * 2.1;
        e.mesh.scale.setScalar(Math.max(0.001, scale));
        e.mesh.rotation.y += dt * 18;
        e.mesh.position.y += dt * 3;
      } else {
        e.mesh.position.addScaledVector(e.velocity, dt);
        e.velocity.multiplyScalar(1 - dt * 3);
        e.mesh.rotation.x += dt * 10;
        e.mesh.scale.setScalar(Math.max(0.001, 1 - t));
      }
      if (t >= 1) {
        scene.remove(e.mesh);
        effects.splice(i, 1);
      }
    }
  }

  // ─── Птица ───────────────────────────────────────────────────────────────
  // Мультяшная low-poly птица из примитивов: своя, без лицензий и файлов.
  // Клюв смотрит в −Z, камера сзади. Крылья — отдельные pivot'ы у плеча,
  // их поворачивает игровой цикл вслед за руками игрока.
  const bird = new THREE.Group();
  const birdColors = {
    body: 0x5fb6f5,
    belly: 0xffe27a,
    wing: 0x4aa6f0,
    wingTip: 0x2f7ad8,
    beak: 0xff9f43,
    eye: 0xffffff,
    pupil: 0x1b1b2f,
    cheek: 0xff6f91,
  };
  const birdMat = mat(birdColors.body);

  function ellipsoid(rx, ry, rz, material, detail = 1) {
    const m = new THREE.Mesh(new THREE.IcosahedronGeometry(1, detail), material);
    m.scale.set(rx, ry, rz);
    return m;
  }

  // Тело — вытянутая «капля», снизу светлое брюшко.
  const body = ellipsoid(0.85, 0.8, 1.35, birdMat);
  bird.add(body);
  const belly = ellipsoid(0.62, 0.55, 1.05, mat(birdColors.belly));
  belly.position.set(0, -0.32, -0.1);
  bird.add(belly);

  // Голова крупная — так птица читается как детская, а не хищная.
  const head = ellipsoid(0.72, 0.7, 0.72, birdMat);
  head.position.set(0, 0.55, -1.25);
  bird.add(head);

  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.6, 5), mat(birdColors.beak));
  beak.rotation.x = -Math.PI / 2;
  beak.position.set(0, 0.45, -2.0);
  bird.add(beak);

  // Глаза и щёчки по бокам головы — камера сзади и чуть сверху их видит.
  [-1, 1].forEach((side) => {
    const eye = ellipsoid(0.2, 0.24, 0.2, mat(birdColors.eye));
    eye.position.set(side * 0.5, 0.72, -1.55);
    bird.add(eye);
    const pupil = ellipsoid(0.11, 0.13, 0.11, mat(birdColors.pupil));
    pupil.position.set(side * 0.6, 0.72, -1.63);
    bird.add(pupil);
    const cheek = ellipsoid(0.13, 0.08, 0.13, mat(birdColors.cheek));
    cheek.position.set(side * 0.58, 0.42, -1.5);
    bird.add(cheek);
  });

  // Хвост веером из трёх перьев.
  [-0.35, 0, 0.35].forEach((angle) => {
    const feather = ellipsoid(0.22, 0.06, 0.75, birdMat, 0);
    feather.position.set(Math.sin(angle) * 0.55, 0.15, 1.55 + Math.cos(angle) * 0.25);
    feather.rotation.y = angle;
    bird.add(feather);
  });

  // Тело целиком неподвижно относительно птицы — одним мешем.
  const bodyMesh = bake(bird, bakedMat, true);
  bird.clear();
  bird.add(bodyMesh);

  // Крыло — как у настоящей птицы: округлое основание у плеча и веер маховых
  // перьев, от светлых к тёмным, кончики отведены назад. Точка вращения у плеча.
  const featherGeo = new THREE.IcosahedronGeometry(1, 1);
  const wingShades = [0x6cc0f7, 0x5ab2f3, 0x479fec, 0x3a8ce2, 0x2f7ad8];

  function makeWing(side) {
    const pivot = new THREE.Group();

    // Основание: пухлое, в цвет тела у плеча переходит в синий.
    const base = new THREE.Mesh(featherGeo, mat(birdColors.wing));
    base.scale.set(0.95, 0.16, 0.62);
    base.position.set(side * 0.8, 0.02, -0.05);
    pivot.add(base);

    // Маховые перья: чем дальше от тела, тем длиннее отведены назад.
    wingShades.forEach((color, i) => {
      const t = i / (wingShades.length - 1);
      const feather = new THREE.Mesh(featherGeo, mat(color));
      feather.scale.set(0.2, 0.06, 0.62 + t * 0.28);
      feather.position.set(side * (1.05 + t * 1.45), -0.01 * i, 0.35 + t * 0.35);
      feather.rotation.y = side * (0.35 + t * 0.55);
      pivot.add(feather);
    });

    // Кроющие перья сверху — прикрывают стык основания и веера.
    const coverts = new THREE.Mesh(featherGeo, mat(birdColors.wing));
    coverts.scale.set(0.75, 0.1, 0.42);
    coverts.position.set(side * 1.5, 0.07, 0.12);
    coverts.rotation.y = side * 0.25;
    pivot.add(coverts);

    // Перья крыла двигаются только вместе — одним мешем на pivot'е.
    const wingMesh = bake(pivot, bakedMat, true);
    pivot.clear();
    pivot.add(wingMesh);

    pivot.position.set(side * 0.65, 0.2, -0.2);
    bird.add(pivot);
    return pivot;
  }
  let wingL = makeWing(-1);
  let wingR = makeWing(1);
  if (options.birdModel) loadBirdModel(options.birdModel);

  // Модель из файла цельная — режем её по размаху: треугольники дальше
  // плеча уходят в левое/правое крыло со своим pivot'ом у плеча. Дальше
  // крылья двигаются тем же кодом, что у птицы из примитивов.
  function loadBirdModel(model: BirdFlightModel): void {
    new GLTFLoader().load(model.url, (gltf) => {
      if (destroyed) return;
      const orient = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(model.pitch, Math.PI, 0, 'YXZ'));
      gltf.scene.updateMatrixWorld(true);
      const sources: { geometry: THREE.BufferGeometry; map: THREE.Texture | null }[] = [];
      const bounds = new THREE.Box3();
      gltf.scene.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (!mesh.isMesh) return;
        const geometry = mesh.geometry.toNonIndexed();
        geometry.applyMatrix4(new THREE.Matrix4().multiplyMatrices(orient, mesh.matrixWorld));
        geometry.computeBoundingBox();
        bounds.union(geometry.boundingBox);
        const map = (mesh.material as THREE.MeshStandardMaterial).map ?? null;
        if (map) map.colorSpace = THREE.SRGBColorSpace;
        sources.push({ geometry, map });
      });
      if (!sources.length) return;
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      const k = CFG.birdSpan / size.x;
      const shoulder = (size.x / 2) * CFG.birdShoulder * k;

      bird.children.forEach((child) => { child.visible = false; });

      // Центрируем и масштабируем все меши, прежде чем резать.
      sources.forEach(({ geometry }) => {
        const arr = geometry.attributes.position.array as Float32Array;
        for (let i = 0; i < arr.length; i += 1) arr[i] = (arr[i] - center.getComponent(i % 3)) * k;
      });

      // Корень крыла — средняя точка вершин в узкой полосе сразу за плечом.
      // Вокруг неё крыло и вращается: с центром птицы (y = z = 0) основание
      // крыла при взмахе уходило от тела, и появлялась щель.
      const strip = shoulder * 0.35;
      const roots = [-1, 1].map((side) => {
        const sum = new THREE.Vector3();
        let count = 0;
        sources.forEach(({ geometry }) => {
          const pos = geometry.attributes.position;
          for (let v = 0; v < pos.count; v += 1) {
            const x = pos.getX(v) * side;
            if (x > shoulder && x < shoulder + strip) {
              sum.add(new THREE.Vector3(pos.getX(v), pos.getY(v), pos.getZ(v)));
              count += 1;
            }
          }
        });
        return count ? sum.divideScalar(count).setX(side * shoulder) : new THREE.Vector3(side * shoulder, 0, 0);
      });
      const pivots = roots.map((root) => {
        const pivot = new THREE.Group();
        pivot.position.copy(root);
        bird.add(pivot);
        return pivot;
      });

      // Крыло забирает и треугольники чуть внутри плеча — они же остаются в
      // теле: крыло заходит под тело, и при взмахе шов не просвечивает.
      const overlap = shoulder * 0.8;
      sources.forEach(({ geometry, map }) => {
        const material = new THREE.MeshLambertMaterial({ map, side: THREE.DoubleSide, emissive: 0x222222 });
        const pos = geometry.attributes.position;
        const parts = { body: [] as number[], left: [] as number[], right: [] as number[] };
        for (let t = 0; t < pos.count; t += 3) {
          const cx = (pos.getX(t) + pos.getX(t + 1) + pos.getX(t + 2)) / 3;
          if (Math.abs(cx) <= shoulder) parts.body.push(t);
          if (cx < -overlap) parts.left.push(t);
          if (cx > overlap) parts.right.push(t);
        }
        const build = (triangles: number[], offset: THREE.Vector3): THREE.BufferGeometry => {
          const out = new THREE.BufferGeometry();
          Object.entries(geometry.attributes).forEach(([name, attr]) => {
            const src = attr as THREE.BufferAttribute;
            const data = new Float32Array(triangles.length * 3 * src.itemSize);
            triangles.forEach((t, n) => {
              for (let v = 0; v < 3; v += 1) {
                for (let c = 0; c < src.itemSize; c += 1) {
                  const shift = name === 'position' ? offset.getComponent(c) : 0;
                  data[(n * 3 + v) * src.itemSize + c] = src.getComponent(t + v, c) - shift;
                }
              }
            });
            out.setAttribute(name, new THREE.BufferAttribute(data, src.itemSize));
          });
          if (!out.attributes.normal) out.computeVertexNormals();
          return out;
        };
        bird.add(new THREE.Mesh(build(parts.body, new THREE.Vector3()), material));
        pivots[0].add(new THREE.Mesh(build(parts.left, roots[0]), material));
        pivots[1].add(new THREE.Mesh(build(parts.right, roots[1]), material));
      });
      [wingL, wingR] = pivots;
      needsRender = true;
    });
  }
  // Карта строится по шагам (генератор, см. pumpWorld в конце): между
  // шагами страница успевает нарисовать экран загрузки. Пока мир не готов,
  // `world` пуст, а игровой цикл стоит. Свет, небо и туман ставит сама карта.
  let world: BirdFlightWorld | null = null;
  const builders: Record<BirdFlightMap, typeof buildAlmatyWorld> = {
    ekibastuz: buildEkibastuzWorld,
    astana: buildAstanaWorld,
    almaty: buildAlmatyWorld,
    barcelona: buildBarcelonaWorld,
    paris: buildParisWorld,
  };
  // Неизвестный id (старое приложение со своим списком карт) — карта по умолчанию.
  const mapId = options.map && Object.hasOwn(builders, options.map) ? options.map : BIRD_FLIGHT_DEFAULT_MAP;
  let worldBuild: Generator<number, BirdFlightWorld, void> | null = builders[mapId](scene);
  function placeInWorld(w: BirdFlightWorld): void {
    camera.far = 1600; // небо открытого мира — сфера 1400 м вокруг камеры
    camera.updateProjectionMatrix();
    bird.rotation.order = 'YXZ';
    bird.position.set(w.spawn.x, w.spawn.y, w.spawn.z);
    camera.position.set(w.spawn.x, w.spawn.y + 6, w.spawn.z + 16);
  }
  scene.add(bird);

  // ─── Ввод: клавиатура + поза ─────────────────────────────────────────────
  // flapPower — сила взмахов с прошлого кадра, копится и тратится в tick.
  // wingUpLeft/Right — насколько поднята рука игрока (−1 внизу … 1 вверху),
  // крылья птицы повторяют её; poseAt — когда пришла последняя поза.
  // spread — раскрытие рук 0…1, крылья птицы раскрываются так же.
  const input = {
    roll: 0, flapPower: 0, source: 'keys', wingUpLeft: 0, wingUpRight: 0, poseAt: -Infinity, spread: 1, turbo: 0,
  };

  const keys = new Set<string>();
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!e.repeat && (e.code === 'Space' || e.code === 'ArrowUp')) input.flapPower += 1;
    if (!e.repeat && e.code === 'Enter') finish(); // как «Закончить» в приложении
    keys.add(e.code);
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(e.code);
  };
  if (options.keyboard) {
    addEventListener('keydown', onKeyDown);
    addEventListener('keyup', onKeyUp);
  }

  function readKeyboard(): void {
    if (input.source !== 'keys') return;
    input.roll = (keys.has('ArrowRight') ? 1 : 0) - (keys.has('ArrowLeft') ? 1 : 0);
    input.turbo = keys.has('ShiftLeft') || keys.has('ShiftRight') ? 1 : 0;
    input.spread = keys.has('ArrowDown') ? 0 : 1;
  }

  // Точки рук поверх игры — игрок видит, что камера его руки поймала.
  // Позы приходят до 30 раз/с, а на слабом ТВ рядом крутится WebGL: писать в
  // DOM на каждый кадр позы дорого (чтение размера принуждало к пересчёту
  // раскладки, CSS-переход перезапускался). Кадр позы только запоминает
  // положение, в DOM оно уходит раз за отрисовку (`flushDots`) через transform.
  const dots = [
    { el: dotL, x: 0, y: 0, shown: false, dirty: false },
    { el: dotR, x: 0, y: 0, shown: false, dirty: false },
  ];
  let viewW = 0;
  let viewH = 0;
  function measureView(): void {
    viewW = width();
    viewH = height();
  }
  // Точки — нормализованные 0…1: px считаем только здесь, по кешу размера.
  function placeDot(dot: (typeof dots)[number], point: BirdFlightPoint): void {
    dot.x = point.x;
    dot.y = point.y;
    dot.dirty = true;
  }
  // Нет позы дольше DOT_HIDE_MS — руки не видны, точки не должны стоять «призраками».
  const DOT_HIDE_MS = 500;
  function flushDots(now: number): void {
    const stale = now - input.poseAt > DOT_HIDE_MS;
    if (viewW === 0 || viewH === 0) measureView();
    for (const dot of dots) {
      if (stale) {
        if (dot.shown) { dot.el.style.display = 'none'; dot.shown = false; }
        continue;
      }
      if (!dot.dirty && dot.shown) continue;
      dot.dirty = false;
      if (!dot.shown) { dot.el.style.display = 'block'; dot.shown = true; }
      dot.el.style.transform = `translate3d(${dot.x * viewW}px,${dot.y * viewH}px,0)`;
    }
  }

  // Взмах — событие, а не сумма дрожи: руки поднялись (запоминаем верхнюю
  // точку), затем ушли вниз на flapMinStroke — один толчок. Следующий
  // засчитается, когда руки снова поднимутся на flapRearm.
  const stroke = { armed: true, top: Infinity, bottom: -Infinity };
  function trackFlap(wristY: number, shoulderSpan: number): void {
    if (stroke.armed) {
      stroke.top = Math.min(stroke.top, wristY);
      const down = (wristY - stroke.top) / shoulderSpan;
      if (down >= CFG.flapMinStroke) {
        input.flapPower += Math.min(down, CFG.flapMaxStroke);
        stroke.armed = false;
        stroke.bottom = wristY;
      }
    } else {
      stroke.bottom = Math.max(stroke.bottom, wristY);
      if ((stroke.bottom - wristY) / shoulderSpan >= CFG.flapRearm) {
        stroke.armed = true;
        stroke.top = wristY;
      }
    }
  }
  function resetStroke(): void {
    Object.assign(stroke, { armed: true, top: Infinity, bottom: -Infinity });
  }

  // Круги руками: у каждой руки — угол запястья вокруг плеча и пройденный
  // угол со знаком (затухает). Турбо — по руке, которая крутит сильнее.
  const spins = [
    { angle: NaN, spin: 0, at: 0 },
    { angle: NaN, spin: 0, at: 0 },
  ];
  function trackSpin(hand: (typeof spins)[number], wrist: BirdFlightPoint, shoulder: BirdFlightPoint, span: number, now: number): void {
    const dt = hand.at ? Math.min(0.2, (now - hand.at) / 1000) : 0;
    hand.at = now;
    hand.spin *= Math.exp(-dt / CFG.turboSpinMemory);
    const dx = wrist.x - shoulder.x, dy = wrist.y - shoulder.y;
    if (Math.hypot(dx, dy) / span < CFG.turboMinRadius) { hand.angle = NaN; return; }
    const angle = Math.atan2(dy, dx);
    if (!Number.isNaN(hand.angle)) {
      let d = angle - hand.angle;
      if (d > Math.PI) d -= Math.PI * 2;
      if (d < -Math.PI) d += Math.PI * 2;
      hand.spin += d;
    }
    hand.angle = angle;
  }
  function resetSpin(): void {
    spins.forEach((hand) => Object.assign(hand, { angle: NaN, spin: 0, at: 0 }));
    input.turbo = 0;
  }

  function applyPose(p: BirdFlightPose): void {
    if (!p || !p.leftWrist || !p.rightWrist || !p.leftShoulder || !p.rightShoulder) return;
    const now = performance.now();
    // После перерыва старая верхняя точка рук ничего не значит: иначе разница
    // «до выхода из кадра / после» засчиталась бы одним огромным взмахом.
    if (now - input.poseAt > CFG.poseTimeoutMs) { resetStroke(); resetSpin(); }
    input.source = 'pose';
    const shoulderSpan = Math.max(0.05, Math.abs(p.leftShoulder.x - p.rightShoulder.x));
    const wristY = (p.leftWrist.y + p.rightWrist.y) / 2;

    // Руки берём по положению на экране, а не по ярлыкам left/right: на
    // зеркальном кадре Vision их путает, и птица летела в обратную сторону.
    const [screenLeft, screenRight] = p.leftWrist.x < p.rightWrist.x
      ? [p.leftWrist, p.rightWrist]
      : [p.rightWrist, p.leftWrist];
    // Крен: какая рука выше, в ширинах плеч — от расстояния до камеры не зависит.
    const tilt = (screenRight.y - screenLeft.y) / shoulderSpan;

    // Крылья повторяют руки: подъём запястья над своим плечом.
    const [shoulderLeft, shoulderRight] = p.leftShoulder.x < p.rightShoulder.x
      ? [p.leftShoulder, p.rightShoulder]
      : [p.rightShoulder, p.leftShoulder];
    input.wingUpLeft = THREE.MathUtils.clamp((shoulderLeft.y - screenLeft.y) / shoulderSpan, -1, 1);
    input.wingUpRight = THREE.MathUtils.clamp((shoulderRight.y - screenRight.y) / shoulderSpan, -1, 1);
    input.poseAt = now;
    const tiltOutside = Math.max(0, Math.abs(tilt) - CFG.rollDeadzone) * Math.sign(tilt);
    input.roll = THREE.MathUtils.clamp(tiltOutside * 1.2, -1, 1);

    // Раскрытие: расстояние между запястьями в ширинах плеч.
    const wristGap = Math.hypot(screenRight.x - screenLeft.x, screenRight.y - screenLeft.y) / shoulderSpan;
    input.spread = THREE.MathUtils.clamp((wristGap - CFG.spreadClosed) / (CFG.spreadOpen - CFG.spreadClosed), 0, 1);

    trackFlap(wristY, shoulderSpan);
    trackSpin(spins[0], screenLeft, shoulderLeft, shoulderSpan, now);
    trackSpin(spins[1], screenRight, shoulderRight, shoulderSpan, now);
    const spin = Math.max(Math.abs(spins[0].spin), Math.abs(spins[1].spin));
    input.turbo = THREE.MathUtils.clamp((spin - CFG.turboSpinStart) / (CFG.turboSpinFull - CFG.turboSpinStart), 0, 1);
    placeDot(dots[0], p.leftWrist);
    placeDot(dots[1], p.rightWrist);
  }

  // ─── Открытый мир ────────────────────────────────────────────────────────
  // Крен поворачивает, птица летит по курсу. У края острова курс плавно
  // заворачивает к центру — улететь из мира нельзя. Звёзды: магнит и подбор.
  function flyOpenWorld(w: BirdFlightWorld, speed: number, dt: number, dive: number): void {
    state.yaw -= state.roll * CFG.turnRate * dt;
    const distFromCenter = Math.hypot(bird.position.x, bird.position.z);
    if (distFromCenter > w.radius) {
      const toCenter = Math.atan2(bird.position.x, bird.position.z);
      const diff = Math.atan2(Math.sin(toCenter - state.yaw), Math.cos(toCenter - state.yaw));
      state.yaw += Math.sign(diff) * Math.min(Math.abs(diff), 1.5 * dt);
    }
    bird.position.x -= Math.sin(state.yaw) * speed * dt;
    bird.position.z -= Math.cos(state.yaw) * speed * dt;
    bird.position.y += state.vy * dt;
    const floor = w.height(bird.position.x, bird.position.z) + CFG.worldMinAltitude;
    if (bird.position.y <= floor) { bird.position.y = floor; state.vy = Math.max(state.vy, 0); }
    if (bird.position.y >= w.maxY) { bird.position.y = w.maxY; state.vy = Math.min(state.vy, 0); }
    void dive;

    for (const p of w.pickups) {
      if (!p.visible) continue;
      const gap = p.position.distanceTo(bird.position);
      if (gap < CFG.magnetRadius && gap > 0.01) {
        p.position.add(bird.position.clone().sub(p.position).multiplyScalar(Math.min(gap, CFG.magnetStrength * dt) / gap));
      }
      if (gap < CFG.pickupRadius) {
        p.visible = false;
        state.apples++;
        collectEffect(p.position.clone());
      }
    }
    // Все звёзды карты собраны — забег окончен (испытание «собери все»).
    if (w.pickups.length > 0 && state.apples >= w.pickups.length) finish();
  }

  // ─── Подсказки ───────────────────────────────────────────────────────────
  // Первые секунды забега по очереди: парить, махать, нырять, поворачивать.
  const hintList = [options.hints?.glide, options.hints?.flap, options.hints?.dive, options.hints?.turn]
    .filter((text): text is string => Boolean(text));
  function updateHint(): void {
    const slot = CFG.hintSeconds / Math.max(1, hintList.length);
    const index = Math.floor(state.elapsed / slot);
    const visible = state.running && index < hintList.length;
    if (visible) hud.hint.textContent = hintList[index];
    hud.hint.classList.toggle('bf-show', visible);
  }

  // ─── Игровой цикл ────────────────────────────────────────────────────────
  // Свободный полёт без таймера: забег заканчивает игрок.
  const state = {
    roll: 0,
    lift: 0,
    vy: 0,
    boost: 0,
    turbo: 0,
    distance: 0,
    apples: 0,
    flapPhase: 0,
    flaps: 0,
    streak: 0,
    lastFlapAt: -Infinity,
    spread: 1,
    elapsed: 0,
    flapPulse: 0,
    wingLeft: 0,
    wingRight: 0,
    yaw: 0,
    running: false,
    /** Итог этого забега уже отдан — второй `finish()` молчит до `restart()`. */
    finished: false,
    startedAt: 0,
  };

  function finish(): void {
    // Забег, который ещё не летел (мир строится, калибровка), заканчивать нечем.
    if (state.finished || (!state.running && state.elapsed === 0)) return;
    state.finished = true;
    state.running = false;
    pendingStart = false;
    needsRender = true;
    options.onFinished?.({
      stars: state.apples,
      distance: Math.floor(state.distance),
      flaps: state.flaps,
      durationSeconds: Math.min(MAX_REPORTED_SECONDS, Math.max(1, Math.round(state.elapsed))),
      startedAt: new Date(state.startedAt).toISOString(),
    });
  }

  function restart(): void {
    input.flapPower = 0;
    resetStroke();
    resetSpin();
    Object.assign(state, {
      roll: 0,
      lift: 0,
      vy: 0,
      boost: 0,
      turbo: 0,
      distance: 0,
      apples: 0,
      flaps: 0,
      streak: 0,
      lastFlapAt: -Infinity,
      spread: 1,
      elapsed: 0,
      flapPulse: 0,
      running: false,
      finished: false,
    });
    if (world) {
      bird.position.set(world.spawn.x, world.spawn.y, world.spawn.z);
      state.yaw = world.spawn.yaw;
      world.pickups.forEach((p) => {
        p.visible = true;
        if (p.userData.home) p.position.copy(p.userData.home); else p.userData.home = p.position.clone();
      });
      // Камеру сразу за птицу — иначе первый кадр летит к ней через весь остров.
      camera.position.set(bird.position.x + Math.sin(state.yaw) * 16, bird.position.y + 6, bird.position.z + Math.cos(state.yaw) * 16);
    }
    clock.getDelta();
    needsRender = true;
  }
  const clock = new THREE.Clock();
  const tmp = new THREE.Vector3();

  // Адаптивное качество: средний кадр за ~2 с медленнее 45 fps — разрешение
  // на ступень ниже, до 1. Обратно не повышаем: нагрев сам не пройдёт.
  const quality = { frames: 0, time: 0 };
  function adaptQuality(dt: number): void {
    if (!options.adaptiveQuality || pixelRatio <= 1) return;
    quality.frames++;
    quality.time += dt;
    if (quality.frames < 120) return;
    const slow = quality.time / quality.frames > 1 / 45;
    quality.frames = 0;
    quality.time = 0;
    if (!slow) return;
    pixelRatio = Math.max(1, pixelRatio - 0.25);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width(), height(), false);
  }

  function tick(): void {
    if (destroyed) return;
    rafId = requestAnimationFrame(tick);
    const w = world;
    if (!w) return; // карта ещё строится
    flushDots(performance.now());
    // Мир стоит (калибровка, пауза, итог): кадр уже на экране, рисовать нечего.
    if (!state.running && !needsRender && effects.length === 0) return;
    needsRender = false;
    const dt = state.running ? Math.min(clock.getDelta(), 0.05) : 0;
    if (state.running) adaptQuality(dt);
    readKeyboard();

    // Игрок вышел из кадра: не держим последний крен — иначе птица так и
    // поворачивала бы, пока не упрётся в стену.
    if (input.source === 'pose' && performance.now() - input.poseAt > CFG.poseTimeoutMs) input.roll = 0;

    // Сглаживание ввода: птица плывёт к цели, а не прыгает за шумом позы.
    const k = 1 - Math.exp(-CFG.smoothing * dt);
    state.roll += (input.roll - state.roll) * k;
    state.spread += (input.spread - state.spread) * k;
    if (state.running) state.elapsed += dt;

    // Взмах — толчок вверх и немного скорости. Без взмахов вертикальная
    // скорость приходит к снижению, которое задают руки: раскрыты — плавно
    // планирует, сложены — пике.
    if (state.running && input.flapPower > 0) {
      // Серия: взмахи один за другим копят силу — размахался, значит выше.
      state.streak = state.elapsed - state.lastFlapAt <= CFG.flapStreakWindow ? state.streak + 1 : 0;
      state.lastFlapAt = state.elapsed;
      const streakBonus = Math.min(state.streak * CFG.flapStreakGain, CFG.flapStreakMax);
      const strength = input.flapPower ** CFG.flapStrokePower * (1 + streakBonus);
      state.vy = Math.max(state.vy, 0) + strength * CFG.flapLift;
      state.boost = Math.min(1, state.boost + input.flapPower * 0.3);
      state.flaps++;
      state.flapPulse = 1;
      hud.flaps.classList.remove('bf-bump');
      void hud.flaps.offsetWidth;
      hud.flaps.classList.add('bf-bump');
    }
    input.flapPower = 0;
    const sink = THREE.MathUtils.lerp(CFG.diveSink, CFG.glideSink, state.spread);
    const vyTarget = state.vy > -sink ? -sink : state.vy;
    state.vy += (vyTarget - state.vy) * (1 - Math.exp(-CFG.sinkResponse * dt));
    state.vy = THREE.MathUtils.clamp(state.vy, -CFG.diveSink, CFG.maxClimb);
    state.lift = THREE.MathUtils.clamp(state.vy / 10, -1, 1);
    state.boost = Math.max(0, state.boost - dt * 0.8);
    state.flapPulse = Math.max(0, state.flapPulse - dt * 3);

    // Турбо разгоняется за ~0,5 с и гаснет за ~1 с, когда круги кончились.
    const turboTarget = state.running ? input.turbo : 0;
    state.turbo += (turboTarget - state.turbo) * (1 - Math.exp(-(turboTarget > state.turbo ? 4 : 1.5) * dt));
    const fov = 62 + CFG.turboFov * state.turbo;
    if (Math.abs(camera.fov - fov) > 0.05) { camera.fov = fov; camera.updateProjectionMatrix(); }

    const dive = 1 - state.spread;
    const speed = (CFG.speed + (CFG.boostSpeed - CFG.speed) * state.boost + CFG.diveSpeedBonus * dive
      + CFG.turboSpeed * state.turbo)
      * CFG.worldSpeedScale;
    state.distance += speed * dt;

    flyOpenWorld(w, speed, dt, dive);

    // Поза птицы следует за вводом: крен и тангаж.
    bird.rotation.z = -state.roll * 0.7;
    bird.rotation.x = state.lift * 0.35 - dive * 0.35;
    bird.rotation.y = state.yaw;

    // Крылья. С позой — повторяют руки игрока: машет он, машет и птица, замер —
    // замерла. Без позы (клавиатура, игрок вышел из кадра) — прежняя анимация.
    // rotation.z > 0 опускает левое крыло, у правого знак обратный.
    const followsPlayer = performance.now() - input.poseAt < 500;
    let targetLeft; let
      targetRight;
    if (followsPlayer) {
      targetLeft = input.wingUpLeft * CFG.wingSwing;
      targetRight = input.wingUpRight * CFG.wingSwing;
    } else {
      // Клавиатура: взмах — короткий мах крыльями, иначе крылья держат позу.
      const flap = -Math.sin(state.flapPulse * Math.PI) * 0.8;
      targetLeft = targetRight = flap;
    }
    // Сложенные руки — сложенные крылья: размах уменьшается к телу.
    const wingScale = THREE.MathUtils.lerp(CFG.foldedWingScale, 1, state.spread);
    wingL.scale.x = wingScale;
    wingR.scale.x = wingScale;
    // Сглаживание быстрое: руки поза отдаёт 30 раз/с, экран рисует чаще.
    const wingK = 1 - Math.exp(-18 * Math.max(dt, 1 / 60));
    state.wingLeft += (targetLeft - state.wingLeft) * wingK;
    state.wingRight += (targetRight - state.wingRight) * wingK;
    wingL.rotation.z = -state.wingLeft;
    wingR.rotation.z = state.wingRight;

    // Камера сзади по курсу, чуть выше; смотрит вперёд и немного вниз.
    const back = new THREE.Vector3(Math.sin(state.yaw), 0, Math.cos(state.yaw));
    const camTarget = tmp.copy(bird.position).addScaledVector(back, 16).add(new THREE.Vector3(0, 6, 0));
    camera.position.lerp(camTarget, 1 - Math.exp(-5 * (dt || 1 / 60)));
    camera.lookAt(bird.position.x - back.x * 30, bird.position.y - 3, bird.position.z - back.z * 30);
    camera.rotation.z += -state.roll * 0.08;
    // Сутки идут по кругу: день → ночь с огнями → снова день.
    const dayPhase = (1 - Math.cos((state.elapsed / CFG.dayCycleSeconds) * Math.PI * 2)) / 2;
    w.update(dt || 1 / 60, state.elapsed, THREE.MathUtils.lerp(0.1, 1, dayPhase), camera.position);

    hud.score.textContent = `⭐ ${state.apples}`;
    hud.dist.textContent = meters(Math.floor(state.distance));
    const streakAlive = state.streak >= 1 && state.elapsed - state.lastFlapAt <= CFG.flapStreakWindow;
    hud.flaps.textContent = `🪶 ${state.flaps}${streakAlive ? ` ×${state.streak + 1}` : ''}${state.turbo > 0.3 ? ' ⚡' : ''}`;
    const ground = w.height(bird.position.x, bird.position.z);
    // Шкала — абсолютная высота от нуля до потолка: над карьером и холмами
    // маркер не прыгает вместе с рельефом (ниже нуля — у нижнего края).
    const altitude = THREE.MathUtils.clamp(bird.position.y / w.maxY, 0, 1);
    altMark.style.top = `${(1 - altitude) * 100}%`;
    altText.textContent = meters(Math.round(bird.position.y));
    hud.alt.classList.toggle('bf-low', state.running
      && bird.position.y - ground < CFG.lowAltitude + CFG.worldMinAltitude);
    updateHint();

    // После финиша dt = 0, а эффекты последней звезды должны доиграть.
    updateEffects(dt || 1 / 60);
    renderer.render(scene, camera);
  }
  tick();

  function resize(): void {
    measureView();
    for (const dot of dots) dot.dirty = true;
    camera.aspect = width() / height();
    camera.updateProjectionMatrix();
    renderer.setSize(width(), height(), false);
    needsRender = true;
  }
  addEventListener('resize', resize);

  // ─── Постройка карты по шагам ────────────────────────────────────────────
  // Шаг генератора → пауза в макрозадачу → следующий шаг. Пауза даёт браузеру
  // отрисовать прогресс (свой на ТВ, мостом в приложение на телефоне).
  let ready = false;
  let pendingStart = false;
  function beginRun(): void {
    restart();
    state.running = true;
    state.startedAt = Date.now();
  }
  function worldReady(): void {
    ready = true;
    needsRender = true;
    options.onLoadProgress?.(1);
    options.onWorldReady?.();
    if (pendingStart) { pendingStart = false; beginRun(); }
  }
  function pumpWorld(): void {
    if (destroyed || !worldBuild) return;
    const step = worldBuild.next();
    if (step.done) {
      world = step.value;
      worldBuild = null;
      placeInWorld(world);
      restart();
      worldReady();
      return;
    }
    options.onLoadProgress?.(step.value as number);
    setTimeout(pumpWorld, 0);
  }
  setTimeout(pumpWorld, 0);

  return {
    setPose: applyPose,
    start: () => { if (ready) beginRun(); else pendingStart = true; },
    pause: () => { pendingStart = false; state.running = false; },
    resume: () => {
      if (state.finished) return; // законченный забег продолжает только `start()`
      if (!ready) { pendingStart = true; return; }
      clock.getDelta(); state.running = true;
    },
    reset: () => { if (ready) restart(); },
    finish,
    isRunning: () => state.running,
    isReady: () => ready,
    resize,
    destroy: () => {
      destroyed = true;
      cancelAnimationFrame(rafId);
      removeEventListener('resize', resize);
      removeEventListener('keydown', onKeyDown);
      removeEventListener('keyup', onKeyUp);
      renderer.dispose();
      root.remove();
    },
  };
}
